import { useEffect, useState } from "react";

type MediaData = {
  delivery: string;
  width: string;
  height: string;
  type: string;
  bitrate: string;
  url: string;
};

type UseMediaFileReturn = {
  mediaData: MediaData | null;
  error: string | null;
};

const useMediaFile = (xmlUrl: string | null): UseMediaFileReturn => {
  const [mediaData, setMediaData] = useState<MediaData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const parseXML = (xmlString: string): MediaData | null => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, "application/xml");

      const parseError = xmlDoc.querySelector("parsererror");
      if (parseError) {
        throw new Error(parseError.textContent || "XML parsing error");
      }

      const mediaFile = xmlDoc.querySelector("MediaFile");
      if (!mediaFile) {
        throw new Error("No <MediaFile> element found in the XML.");
      }

      const delivery = mediaFile.getAttribute("delivery") || "";
      const width = mediaFile.getAttribute("width") || "";
      const height = mediaFile.getAttribute("height") || "";
      const type = mediaFile.getAttribute("type") || "";
      const bitrate = mediaFile.getAttribute("bitrate") || "";

      const cdata = mediaFile.textContent?.trim() || "";

      return { delivery, width, height, type, bitrate, url: cdata };
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  useEffect(() => {
    const fetchXML = async (url: string) => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const xmlString = await response.text();
        const parsedData = parseXML(xmlString);
        setMediaData(parsedData);
      } catch (err: any) {
        setError(err.message || "Error fetching or parsing XML.");
      }
    };

    if (xmlUrl) {
      fetchXML(xmlUrl);
    }
  }, [xmlUrl]);

  return { mediaData, error };
};

export default useMediaFile;
